import { useEffect, useState } from 'react';
import { collection, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { firestore, functionsEu, httpsCallable } from '../firebase';
import type { ChatItem } from '../types';

export type PartnerCommunityRole = 'admin' | 'co-admin' | 'member';
type JoinResult = { communityId: string; joined: boolean; role: PartnerCommunityRole };
export type PartnerCommunityMember = {
  userId: string;
  name: string;
  photo?: string;
  role: PartnerCommunityRole;
  status: 'active' | 'left' | 'removed' | 'eligible';
};

export const joinPartnerCommunity = async (venueId: string, follow = false) => {
  const call = httpsCallable<{ venueId: string; follow: boolean }, JoinResult>(functionsEu, 'joinPartnerCommunityEu');
  return (await call({ venueId, follow })).data;
};

export const leavePartnerCommunity = async (venueId: string) => {
  const call = httpsCallable<{ venueId: string }, { left: boolean }>(functionsEu, 'leavePartnerCommunityEu');
  return (await call({ venueId })).data;
};

export const removePartnerCommunityMember = async (venueId: string, userId: string) => {
  const call = httpsCallable<{ venueId: string; userId: string }, { removed: boolean }>(functionsEu, 'removePartnerCommunityMemberEu');
  return (await call({ venueId, userId })).data;
};

export const reinstatePartnerCommunityMember = async (venueId: string, userId: string) => {
  const call = httpsCallable<{ venueId: string; userId: string }, { reinstated: boolean; rejoined: boolean }>(functionsEu, 'reinstatePartnerCommunityMemberEu');
  return (await call({ venueId, userId })).data;
};

export const listPartnerCommunityMembers = async (venueId: string) => {
  const call = httpsCallable<{ venueId: string }, { members: PartnerCommunityMember[] }>(functionsEu, 'listPartnerCommunityMembersEu');
  return (await call({ venueId })).data.members;
};

export const setPartnerCommunityCoAdmin = async (venueId: string, userId: string, enabled: boolean) => {
  const call = httpsCallable<{ venueId: string; userId: string; enabled: boolean }, { userId: string; role: PartnerCommunityRole }>(functionsEu, 'setPartnerCommunityCoAdminEu');
  return (await call({ venueId, userId, enabled })).data;
};

export const updatePartnerCommunityDescription = async (venueId: string, description: string) => {
  const call = httpsCallable<{ venueId: string; description: string }, { description: string }>(functionsEu, 'updatePartnerCommunityDescriptionEu');
  return (await call({ venueId, description })).data;
};

const millis = (value: any) => value?.toMillis?.() ?? (value?.seconds ? value.seconds * 1000 : 0);
const EMPTY_IDS = new Set<string>();

/** Live community rows are discovered through a server-owned per-user mirror;
 * each parent chat then has its own listener for latest message/unread UI. */
export function usePartnerCommunities(userId: string | null, mutedIds: Set<string> = EMPTY_IDS, viewerIsAdmin = false) {
  const [communities, setCommunities] = useState<ChatItem[]>([]);
  const [loading, setLoading] = useState(Boolean(userId));

  useEffect(() => {
    if (!userId) {
      setCommunities([]);
      setLoading(false);
      return undefined;
    }
    let chatUnsubs: Array<() => void> = [];
    const rows = new Map<string, ChatItem>();
    const publish = () => setCommunities([...rows.values()].sort((a, b) => b.lastActivityAtMs - a.lastActivityAtMs));
    const unsubscribe = onSnapshot(
      collection(firestore, 'users', userId, 'partnerCommunities'),
      snapshot => {
        chatUnsubs.forEach(stop => stop());
        chatUnsubs = [];
        rows.clear();
        if (snapshot.empty) {
          setCommunities([]);
          setLoading(false);
          return;
        }
        snapshot.docs.forEach(mirror => {
          const mirrorData = mirror.data();
          chatUnsubs.push(onSnapshot(doc(firestore, 'partnerCommunityChats', mirror.id), chat => {
            if (!chat.exists()) { rows.delete(mirror.id); publish(); return; }
            const data = chat.data();
            if (data.adminOnly === true && data.ownerUid !== userId && !viewerIsAdmin) {
              rows.delete(mirror.id);
              publish();
              return;
            }
            const activityMs = millis(data.lastActivityAt || data.lastMessageAt || data.updatedAt || data.createdAt);
            const readMs = millis(data.lastReadAt?.[userId]);
            const unread = Boolean(data.lastMessageSenderId && data.lastMessageSenderId !== userId && activityMs > readMs);
            rows.set(mirror.id, {
              id: mirror.id,
              name: data.name || mirrorData.name || 'Partner community',
              photo: data.photo || mirrorData.photo || '',
              lastMsg: data.lastMessage || '',
              time: '',
              lastActivityAtMs: activityMs,
              unread,
              unreadCount: unread ? 1 : 0,
              lastMessageSenderId: data.lastMessageSenderId,
              lastReadAt: data.lastReadAt || {},
              participantIds: [],
              kind: 'community',
              venueId: mirror.id,
              communityRole: mirrorData.role === 'admin' || mirrorData.role === 'co-admin' ? mirrorData.role : 'member',
              isMuted: mutedIds.has(mirror.id),
            });
            publish();
            setLoading(false);
          }, () => {
            rows.delete(mirror.id);
            publish();
            setLoading(false);
          }));
        });
      },
      () => setLoading(false),
    );
    return () => {
      unsubscribe();
      chatUnsubs.forEach(stop => stop());
    };
  }, [mutedIds, userId, viewerIsAdmin]);

  return { communities, loading };
}

export async function isPartnerCommunityMember(userId: string, venueId: string) {
  return (await getDoc(doc(firestore, 'users', userId, 'partnerCommunities', venueId))).exists();
}
